// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Visao360.tsx
//  Visão 360° do cliente — painel read-only de inteligência de CRM.
//  Espelha o FrmVisao360Cliente desktop (timeline + lead score + resumo).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Target, Activity, ShoppingCart, MessageSquare, TrendingUp } from 'lucide-react';

import { crmService } from '@/features/operacional/services/crmService';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const FAIXA_COR: Record<string, string> = {
  Quente: 'text-emerald-600',
  Morno:  'text-amber-600',
  Frio:   'text-rose-600',
};

const TIPO_COR: Record<string, string> = {
  Oportunidade: 'bg-blue-500/10 text-blue-600',
  Atendimento:  'bg-violet-500/10 text-violet-600',
  Compra:       'bg-emerald-500/10 text-emerald-600',
};

function formatarData(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

const Visao360 = () => {
  const [clienteId, setClienteId] = useState<number | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['visao360', clienteId],
    queryFn: () => crmService.visao360(clienteId!),
    enabled: !!clienteId,
  });

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="h-6 w-6" />
          Visão 360° do Cliente
        </h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          Histórico consolidado, lead score e resumo de relacionamento.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 max-w-xl">
          <label className="text-sm font-medium">Cliente</label>
          <div className="mt-1">
            <PessoaSearchCombobox
              value={clienteId}
              onChange={setClienteId}
              placeholder="Buscar cliente..."
              filter="cliente"
            />
          </div>
        </CardContent>
      </Card>

      {!clienteId && (
        <p className="text-muted-foreground text-sm">Selecione um cliente para ver a visão 360°.</p>
      )}

      {clienteId && (isLoading || isFetching) && (
        <p className="text-muted-foreground text-sm">Carregando...</p>
      )}

      {clienteId && data && (
        <>
          {/* Score + Resumo */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground font-normal">Lead Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className={`text-4xl font-bold ${FAIXA_COR[data.score.faixa] ?? ''}`}>
                    {data.score.score}
                  </span>
                  <Badge variant="outline" className={FAIXA_COR[data.score.faixa] ?? ''}>
                    {data.score.faixa}
                  </Badge>
                </div>
                {data.score.fatores.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                    {data.score.fatores.map((f, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span>•</span><span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground font-normal">Resumo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <ResumoItem icon={<Target className="h-4 w-4" />} label="Oportunidades" valor={data.resumo.oportunidades} />
                  <ResumoItem icon={<MessageSquare className="h-4 w-4" />} label="Atendimentos" valor={data.resumo.atendimentos} />
                  <ResumoItem icon={<ShoppingCart className="h-4 w-4" />} label="Compras" valor={data.resumo.compras} />
                  <ResumoItem icon={<Activity className="h-4 w-4" />} label="Total" valor={data.resumo.total} />
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Última interação: <span className="font-medium">{formatarData(data.resumo.ultimaInteracao)}</span>
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Linha do tempo</CardTitle>
            </CardHeader>
            <CardContent>
              {data.timeline.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground text-sm">
                  Nenhuma interação registrada para este cliente.
                </p>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-40">Data</TableHead>
                        <TableHead className="w-32">Tipo</TableHead>
                        <TableHead>Título</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="w-28">Situação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.timeline.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm whitespace-nowrap">{formatarData(item.data)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={TIPO_COR[item.tipo] ?? ''}>
                              {item.tipo}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm font-medium">{item.titulo}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{item.descricao ?? '—'}</TableCell>
                          <TableCell className="text-sm">{item.situacao ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

function ResumoItem({ icon, label, valor }: { icon: React.ReactNode; label: string; valor: number }) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
        {icon}{label}
      </div>
      <p className="text-2xl font-bold">{valor}</p>
    </div>
  );
}

export default Visao360;
